import React, { useEffect, useState, useCallback } from 'react';
import { extractTeachingStandardId } from '../../utils/mongodb';
import { GRADE_LEVELS_K12, type GradeLevelK12 } from '../../constants/grades';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,

  Snackbar,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  School as SchoolIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as TimeIcon,
  Lightbulb as LightbulbIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import apiAxiosInstance from '../../utils/axiosInstance';
import { useTeacher } from '../../context/TeacherContext';
import { hasReading, hasMath } from '../../utils/subjectHelpers';

type SessionPanelProps = {
  scope: 'student' | 'group' | null;
  id: string | null;
  name?: string;
  onLoadContext: (ctx: { session?: any; notes?: any[]; progress?: any }) => void;
  onSessionCreated?: () => void;
};

const GRADE_LEVELS = [...GRADE_LEVELS_K12];

// Subject options for dropdown - internal value vs display label
const SUBJECT_OPTIONS = [
  { value: 'ELA', label: 'Reading / ELA', check: hasReading },
  { value: 'Math', label: 'Mathematics', check: hasMath },
] as const;

const SessionPanel: React.FC<SessionPanelProps> = ({
  scope,
  id,
  name,
  onLoadContext,
  onSessionCreated,
}) => {
  const { teacher } = useTeacher();
  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSession, setSelectedSession] = useState<any | null>(null);
  const [notes, setNotes] = useState<any[]>([]);
  const [progress, setProgress] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // New session dialog
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const [newSessionTitle, setNewSessionTitle] = useState('');
  const [newSessionFocusSkill, setNewSessionFocusSkill] = useState('');
  const [newSessionFocusStandard, setNewSessionFocusStandard] = useState('');
  const [newSessionDuration, setNewSessionDuration] = useState(20);
  
  // Standards selection
  const [availableStandards, setAvailableStandards] = useState<any[]>([]);
  const [availableGradeLevels, setAvailableGradeLevels] = useState<string[]>(GRADE_LEVELS);
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [loadingStandards, setLoadingStandards] = useState(false);

  // Edit title state
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [editTitleValue, setEditTitleValue] = useState('');

  // Three-dot menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSessionId, setMenuSessionId] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error';
  } | null>(null);

  // Load all sessions for this student/group
  const loadSessions = useCallback(async () => {
    if (!scope || !id) {
      setSessions([]);
      setSelectedSession(null);
      setNotes([]);
      setProgress(null);
      return;
    }

    setLoading(true);
    try {
      const params =
        scope === 'student'
          ? { scope: 'student', studentId: id }
          : { scope: 'group', groupId: id };
      const res = await apiAxiosInstance.get('/api/instruction/sessions', { params });
      const sessionList = res.data || [];
      setSessions(sessionList);

      // Select the first planned session by default, or the first session if none planned
      const plannedSession =
        sessionList.find((s: any) => s.status === 'planned') || sessionList[0];
      if (plannedSession) {
        setSelectedSession(plannedSession);
        await loadSessionDetails(plannedSession._id);
      } else {
        setSelectedSession(null);
        setNotes([]);
        onLoadContext({});
      }

      // Load progress summary
      await loadProgressSummary();
    } catch (e) {
      console.error('Failed to load sessions', e);
    } finally {
      setLoading(false);
    }
  }, [scope, id, onLoadContext]);

  // Load details for a specific session
  const loadSessionDetails = async (sessionId: string) => {
    try {
      const n = await apiAxiosInstance.get(`/api/instruction/sessions/${sessionId}/notes`);
      setNotes(n.data || []);
    } catch (e) {
      console.error('Failed to load session details', e);
    }
  };

  // Load progress summary
  const loadProgressSummary = async () => {
    if (!scope || !id) return;
    try {
      const res = await apiAxiosInstance.get(`/api/instruction/progress/${scope}/${id}/summary`);
      setProgress(res.data);
    } catch (e) {
      console.error('Failed to load progress summary', e);
    }
  };

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Update context when selected session or its data changes
  useEffect(() => {
    if (selectedSession) {
      onLoadContext({ session: selectedSession, notes, progress });
    }
  }, [selectedSession, notes, progress, onLoadContext]);

  // Initialize grade level from teacher profile
  useEffect(() => {
    if (teacher?.gradeLevel && !selectedGrade) {
      const grade = teacher.gradeLevel;
      if (GRADE_LEVELS.includes(grade as GradeLevelK12)) {
        setSelectedGrade(grade);
      }
    }
  }, [teacher?.gradeLevel, selectedGrade]);

  // Fetch available grade levels when dialog opens
  useEffect(() => {
    if (!newSessionOpen) return;
    
    const fetchGradeLevels = async () => {
      try {
        const params: Record<string, string> = {};
        const teachingStandardsId = extractTeachingStandardId(teacher?.teachingStandards);
        if (teachingStandardsId) params.teachingStandards = teachingStandardsId;
        if (selectedSubject) params.subject = selectedSubject;

        const response = await apiAxiosInstance.get('/api/detailedstandards/grade-levels', { params });
        setAvailableGradeLevels(Array.isArray(response.data) ? response.data : GRADE_LEVELS);
      } catch (err) {
        console.error('Error fetching grade levels:', err);
        setAvailableGradeLevels(GRADE_LEVELS);
      }
    };
    
    fetchGradeLevels();
  }, [newSessionOpen, teacher?.teachingStandards, selectedSubject]);

  // Fetch standards when grade or subject changes
  useEffect(() => {
    const fetchStandards = async () => {
      if (!selectedGrade) {
        setAvailableStandards([]);
        return;
      }

      setLoadingStandards(true);
      try {
        const params: Record<string, string> = { gradeLevel: selectedGrade };
        if (selectedSubject) params.subject = selectedSubject;
        
        const teachingStandardsId = extractTeachingStandardId(teacher?.teachingStandards);
        if (teachingStandardsId) params.teachingStandards = teachingStandardsId;

        const response = await apiAxiosInstance.get('/api/detailedstandards', { params });
        setAvailableStandards(response.data || []);
      } catch (err) {
        console.error('Error fetching standards:', err);
        setAvailableStandards([]);
      } finally {
        setLoadingStandards(false);
      }
    };

    fetchStandards();
  }, [selectedGrade, selectedSubject, teacher?.teachingStandards]);

  // Handle creating a new session
  const handleCreateSession = async () => {
    if (!scope || !id) return;
    
    if (!newSessionFocusSkill.trim() && !newSessionFocusStandard.trim()) {
      setToast({ open: true, msg: 'Please enter a focus skill or select a standard', sev: 'error' });
      return;
    }

    try {
      const payload = {
        scope: { type: scope, [`${scope}Id`]: id },
        status: 'planned',
        title: newSessionTitle.trim() || undefined,
        focusSkill: newSessionFocusSkill.trim() || undefined,
        focusStandard: newSessionFocusStandard.trim() || undefined,
        duration: newSessionDuration,
        objectives: [],
        materials: [],
      };
      const res = await apiAxiosInstance.post('/api/instruction/sessions', payload);
      const created = res.data;

      setSessions((prev) => [created, ...prev]);
      setSelectedSession(created);
      await loadSessionDetails(created._id);

      setToast({ open: true, msg: 'New session created', sev: 'success' });
      setNewSessionOpen(false);
      setNewSessionTitle('');
      setNewSessionFocusSkill('');
      setNewSessionFocusStandard('');
      setNewSessionDuration(20);
      setSelectedGrade('');
      setSelectedSubject('');
      setAvailableStandards([]);

      if (onSessionCreated) {
        onSessionCreated();
      }
    } catch (e) {
      setToast({ open: true, msg: 'Failed to create session', sev: 'error' });
    }
  };

  // Handle updating session title
  const handleUpdateTitle = async (sessionId: string) => {
    if (!editTitleValue.trim()) {
      setEditingTitle(null);
      return;
    }

    try {
      await apiAxiosInstance.patch(`/api/instruction/sessions/${sessionId}`, {
        title: editTitleValue.trim(),
      });

      setSessions((prev) =>
        prev.map((s) =>
          s._id === sessionId ? { ...s, title: editTitleValue.trim() } : s
        )
      );
      if (selectedSession?._id === sessionId) {
        setSelectedSession({ ...selectedSession, title: editTitleValue.trim() });
      }

      setToast({ open: true, msg: 'Title updated', sev: 'success' });
      setEditingTitle(null);
    } catch (e) {
      setToast({ open: true, msg: 'Failed to update title', sev: 'error' });
    }
  };

  const startEditingTitle = (s: any) => {
    setEditingTitle(s._id);
    setEditTitleValue(s.title || '');
  };

  const cancelEditingTitle = () => {
    setEditingTitle(null);
    setEditTitleValue('');
  };

  // Three-dot menu handlers
  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, sessionId: string) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuSessionId(sessionId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuSessionId(null);
  };

  const handleDeleteSession = async () => {
    if (!menuSessionId) return;
    
    try {
      await apiAxiosInstance.delete(`/api/instruction/sessions/${menuSessionId}`);
      
      setSessions((prev) => prev.filter((s) => s._id !== menuSessionId));
      if (selectedSession?._id === menuSessionId) {
        setSelectedSession(null);
        setNotes([]);
        onLoadContext({});
      }
      
      setToast({ open: true, msg: 'Session deleted', sev: 'success' });
    } catch (e) {
      setToast({ open: true, msg: 'Failed to delete session', sev: 'error' });
    } finally {
      handleMenuClose();
    }
  };

  const getSessionDisplayTitle = (s: any) => {
    if (s.title) return s.title;
    if (s.focusSkill) return `Session ${s.sessionNumber}: ${s.focusSkill}`;
    return `Session ${s.sessionNumber}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planned': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  if (!scope || !id) {
    return (
      <Paper
        sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Typography variant="h6">Session</Typography>
        <Typography variant="body2" color="text.secondary">
          Select a student or group to view and manage instruction sessions.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="h6">
            {name ? `${name}'s Sessions` : 'Sessions'}
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setNewSessionOpen(true)}
          >
            New Session
          </Button>
        </Box>

        {/* Progress Summary */}
        {progress && progress.totalSessions > 0 && (
          <Accordion sx={{ mb: 2 }} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LightbulbIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2">Progress Summary</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                Total Sessions: {progress.totalSessions}
              </Typography>
              {progress.currentFocus && (
                <Typography variant="body2" color="text.secondary">
                  Current Focus: {progress.currentFocus}
                </Typography>
              )}
              {progress.averageConfidence > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Avg. Confidence: {progress.averageConfidence.toFixed(1)}/5
                </Typography>
              )}
              {progress.recentSkills && progress.recentSkills.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Recent Skills:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {progress.recentSkills.map((skill: string, idx: number) => (
                      <Chip key={idx} label={skill} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Session List */}
        <Typography variant="caption" color="text.secondary" gutterBottom>
          Select a session to view and plan
        </Typography>
        <List dense sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {sessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No sessions yet. Create your first session to get started.
            </Typography>
          ) : (
            sessions.map((s) => (
              <ListItemButton
                key={s._id}
                selected={selectedSession?._id === s._id}
                onClick={() => {
                  setSelectedSession(s);
                  loadSessionDetails(s._id);
                }}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  py: 0.5,
                }}
              >
                {editingTitle === s._id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
                    <TextField
                      size="small"
                      value={editTitleValue}
                      onChange={(e) => setEditTitleValue(e.target.value)}
                      placeholder="Session title..."
                      autoFocus
                      fullWidth
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateTitle(s._id);
                        if (e.key === 'Escape') cancelEditingTitle();
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateTitle(s._id);
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelEditingTitle();
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                            {getSessionDisplayTitle(s)}
                          </Typography>
                          <Chip
                            label={s.status}
                            size="small"
                            sx={{ height: 16, fontSize: '0.65rem' }}
                            color={getStatusColor(s.status)}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          #{s.sessionNumber} • {new Date(s.createdAt).toLocaleDateString()}
                        </Typography>
                      }
                    />
                    <Tooltip title="Edit title">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditingTitle(s);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More options">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, s._id)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </ListItemButton>
            ))
          )}
        </List>

        {/* Three-dot Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem onClick={handleDeleteSession}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {selectedSession && (
          <>
            <Divider sx={{ my: 1 }} />

            {/* Selected Session Info */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Session
              </Typography>
              
              {selectedSession.focusSkill && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Focus:</strong> {selectedSession.focusSkill}
                </Typography>
              )}
              
              {selectedSession.focusStandard && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Standard:</strong> {selectedSession.focusStandard}
                </Typography>
              )}
              
              {selectedSession.duration && (
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TimeIcon fontSize="small" />
                  {selectedSession.duration} minutes
                </Typography>
              )}

              {selectedSession.objectives && selectedSession.objectives.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Objectives:
                  </Typography>
                  <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
                    {selectedSession.objectives.map((obj: string, idx: number) => (
                      <li key={idx}>
                        <Typography variant="caption">{obj}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}

              {selectedSession.materials && selectedSession.materials.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Materials:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {selectedSession.materials.map((mat: string, idx: number) => (
                      <Chip key={idx} label={mat} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}

              {selectedSession.planNotes && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Plan Notes:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedSession.planNotes}
                  </Typography>
                </Box>
              )}
            </Box>

            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => loadSessionDetails(selectedSession._id)}
              sx={{ mt: 1 }}
            >
              Refresh
            </Button>
          </>
        )}
      </Paper>

      {/* New Session Dialog */}
      <Dialog
        open={newSessionOpen}
        onClose={() => setNewSessionOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon />
            Plan New Session
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Session Title (optional)"
              value={newSessionTitle}
              onChange={(e) => setNewSessionTitle(e.target.value)}
              placeholder={`e.g., "Main Idea Practice"`}
              fullWidth
              helperText="Give this session a descriptive title"
            />
            <TextField
              label="Focus Skill"
              value={newSessionFocusSkill}
              onChange={(e) => setNewSessionFocusSkill(e.target.value)}
              placeholder={`e.g., "Identifying Main Idea"`}
              fullWidth
              helperText="What skill will you be working on? (optional if standard is selected)"
            />
            
            {/* Grade Level Selection */}
            <FormControl fullWidth>
              <InputLabel>Grade Level</InputLabel>
              <Select
                value={selectedGrade}
                label="Grade Level"
                onChange={(e) => {
                  setSelectedGrade(e.target.value);
                  setNewSessionFocusStandard(''); // Reset standard when grade changes
                }}
              >
                <MenuItem value="">
                  <em>Select Grade Level</em>
                </MenuItem>
                {availableGradeLevels.map((grade) => (
                  <MenuItem key={grade} value={grade}>
                    Grade {grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Subject Selection - Only show subjects teacher has */}
            <FormControl fullWidth>
              <InputLabel>Subject (optional)</InputLabel>
              <Select
                value={selectedSubject}
                label="Subject (optional)"
                onChange={(e) => {
                  setSelectedSubject(e.target.value);
                  setNewSessionFocusStandard(''); // Reset standard when subject changes
                }}
              >
                <MenuItem value="">
                  <em>All Subjects</em>
                </MenuItem>
                {SUBJECT_OPTIONS.filter((opt) => opt.check(teacher)).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Standards Dropdown */}
            <FormControl fullWidth disabled={!selectedGrade || loadingStandards}>
              <InputLabel>Focus Standard (optional)</InputLabel>
              <Select
                value={newSessionFocusStandard}
                label="Focus Standard (optional)"
                onChange={(e) => setNewSessionFocusStandard(e.target.value)}
                renderValue={(selected) => {
                  if (!selected) return '';
                  const std = availableStandards.find((s: any) => 
                    (s.code || s.standard || s._id) === selected
                  );
                  if (!std) return selected;
                  const code = std.code || std.standard || '';
                  const desc = std.description || '';
                  return desc ? `${code} - ${desc.length > 60 ? desc.substring(0, 60) + '...' : desc}` : code;
                }}
              >
                <MenuItem value="">
                  <em>Select a standard (optional)</em>
                </MenuItem>
                {availableStandards.map((standard: any) => {
                  const code = standard.code || standard.standard || standard.standardIdentifier || '';
                  const description = standard.description || standard.short_description || '';
                  return (
                    <MenuItem 
                      key={standard._id || code} 
                      value={code || standard._id}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
                        {code}{description ? ` - ${description}` : ''}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Select>
              {loadingStandards && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Loading standards...
                </Typography>
              )}
              {selectedGrade && availableStandards.length === 0 && !loadingStandards && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  No standards found for this grade/subject combination
                </Typography>
              )}
            </FormControl>
            
            <TextField
              label="Duration (minutes)"
              type="number"
              value={newSessionDuration}
              onChange={(e) => setNewSessionDuration(parseInt(e.target.value) || 20)}
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewSessionOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSession}
            disabled={!newSessionFocusSkill.trim() && !newSessionFocusStandard.trim()}
            startIcon={<AddIcon />}
          >
            Create Session
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={!!toast?.open}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
      >
        <Alert
          severity={toast?.sev || 'success'}
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast?.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SessionPanel;
